import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { HeaderErrorEnum } from '../enums/header-error.enum';
import { SubUrlsEnum } from '../enums/sub-urls.enum';
import { IAssignClassDtoOut, IClass, IClassDTOIn, IClassDTOOut } from '../interfaces/groups/class.interface';
import { IEntityDTOIn, IEntityFromBack } from '../interfaces/groups/entity.interface';
import { MappersService } from './mappers.service';

@Injectable({
    providedIn: 'root',
})
export class ClassesService {
    constructor(private http: HttpClient, private mapperService: MappersService) {}

    // READ
    public getAllClasses(studentId?: number): Observable<IClass[]> {
        let params: HttpParams = new HttpParams();
        if (studentId) {
            params = params.set('viewing_student_id', studentId);
        }
        return this.http
            .get<IEntityFromBack>(environment.wasoApi + SubUrlsEnum.CLASSES, { params: studentId ? params : null })
            .pipe(
                map((result: IEntityFromBack) =>
                    result.school_classes.map((schoolClass: IEntityDTOIn) =>
                        this.mapperService.castIEntityToIClass(this.mapperService.fromIEntityDTOInToIEntity(schoolClass))
                    )
                )
            );
    }

    public getClassById(id: number): Observable<IClass> {
        return this.http
            .get<IEntityDTOIn>(environment.wasoApi + SubUrlsEnum.CLASSES + '/' + id)
            .pipe(
                map((result: IEntityDTOIn) => this.mapperService.castIEntityToIClass(this.mapperService.fromIEntityDTOInToIEntity(result)))
            );
    }

    // CREATE
    public createClass(newClass: IClassDTOOut): Observable<IClass> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .post<IClassDTOIn>(environment.wasoApi + SubUrlsEnum.CLASSES, newClass, { headers })
            .pipe(
                map((schoolClass: IEntityDTOIn) =>
                    this.mapperService.castIEntityToIClass(this.mapperService.fromIEntityDTOInToIEntity(schoolClass))
                )
            );
    }

    // UPDATE
    public patchClass(schoolClass: IClassDTOOut, id: number): Observable<IClass> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http
            .patch<IClassDTOIn>(environment.wasoApi + SubUrlsEnum.CLASSES + '/' + id, schoolClass, { headers })
            .pipe(
                map((entity: IClassDTOIn) => this.mapperService.castIEntityToIClass(this.mapperService.fromIEntityDTOInToIEntity(entity)))
            );
    }

    // DELETE
    public deleteClass(id: number): Observable<void> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.CLASSES + '/' + id, {
            headers,
        });
    }

    // cruD: DELETE -> ENABLE / DISABLE
    public enableClass(classId: number): Observable<IClass> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        const fullUrl: string = environment.wasoApi + SubUrlsEnum.SCHOOLS + '/' + classId + '/' + SubUrlsEnum.STATUS;
        return this.http
            .post<IEntityDTOIn>(fullUrl, {}, { headers })
            .pipe(
                map((entity: IEntityDTOIn) => this.mapperService.castIEntityToIClass(this.mapperService.fromIEntityDTOInToIEntity(entity)))
            );
    }

    public disableClass(classId: number): Observable<IClass> {
        const headers: HttpHeaders = new HttpHeaders().set(HeaderErrorEnum.OWN_ERROR, 'true');
        const fullUrl: string = environment.wasoApi + SubUrlsEnum.SCHOOLS + '/' + classId + '/' + SubUrlsEnum.STATUS;
        return this.http
            .delete<IEntityDTOIn>(fullUrl, { headers })
            .pipe(
                map((entity: IEntityDTOIn) => this.mapperService.castIEntityToIClass(this.mapperService.fromIEntityDTOInToIEntity(entity)))
            );
    }

    public migrateClasses(schoolId: number, newClasses: IAssignClassDtoOut[]): Observable<any> {
        return this.http.post<any>(environment.wasoApi + SubUrlsEnum.ASSIGN_CLASSES.replace('{{id_school}}', schoolId.toString()), {
            school_class_ids: newClasses,
        });
    }
}
